import { z } from 'zod';

/**
 * Wave 98 — Relatórios customizáveis.
 *
 * A saved report definition is a `ReportConfig` blob plus name/description.
 * Running it returns a row-oriented `ReportResult` where each row has a
 * `group` key (date bucket / stage / role / supplier / 'total') and a
 * `values` map keyed by `ReportMetricKey`. The frontend builds the
 * grouped bar chart from this shape directly.
 */

export const reportMetricKeySchema = z.enum([
  'labor_cost',
  'labor_hours',
  'po_total',
  'receipts_total',
  'budget_committed',
  'budget_paid',
  'budget_variance',
  'payment_total',
  'stage_progress',
  'issue_count',
  'rnc_count',
]);
export type ReportMetricKey = z.infer<typeof reportMetricKeySchema>;

export const reportMetricSchema = z.object({
  key: reportMetricKeySchema,
  label: z.string().optional(),
});
export type ReportMetric = z.infer<typeof reportMetricSchema>;

export const reportDateRangeModeSchema = z.enum([
  'last30', 'last90', 'month', 'quarter', 'custom',
]);
export type ReportDateRangeMode = z.infer<typeof reportDateRangeModeSchema>;

export const reportGroupBySchema = z.enum([
  'day', 'week', 'month', 'stage', 'role', 'supplier', 'none',
]);
export type ReportGroupBy = z.infer<typeof reportGroupBySchema>;

export const reportConfigSchema = z.object({
  projectId: z.string().nullable().optional(),
  dateRange: z.object({
    mode: reportDateRangeModeSchema,
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  }),
  metrics: z.array(reportMetricSchema).min(1).max(11),
  groupBy: reportGroupBySchema,
  sortBy: z.string().optional(),
  sortDir: z.enum(['asc', 'desc']).optional(),
});
export type ReportConfig = z.infer<typeof reportConfigSchema>;

export interface ReportRow {
  group: string;
  values: Record<string, number>;
}

export interface ReportResult {
  rows: ReportRow[];
  totals: Record<string, number>;
  generatedAt: string;
  resolvedRange: { from: string; to: string };
}

export interface ReportDefinition {
  id: string;
  name: string;
  description: string | null;
  config: ReportConfig;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export const saveReportSchema = z.object({
  name: z.string().trim().min(2).max(120),
  description: z.string().max(500).nullable().optional(),
  config: reportConfigSchema,
});
export type SaveReportRequest = z.infer<typeof saveReportSchema>;

export const runReportSchema = z.object({
  config: reportConfigSchema,
});
export type RunReportRequest = z.infer<typeof runReportSchema>;
